import React from "react";
import TagBadge from "./TagBadge";
import { Edit, Trash2, Store } from "lucide-react";
import { Shop } from "../types/Shop";
import { getOptimizedShopIcon, generateImageKitSrcSet } from "../utils/imagekit";

interface ShopCardListProps {
  shops: Shop[];
  onEdit: (shop: Shop) => void;
  onDelete: (id: string) => void;
}

const ShopCardList: React.FC<ShopCardListProps> = ({ shops, onEdit, onDelete }) => (
  <div className="lg:hidden space-y-4">
    {shops.map((shop) => (
      <div key={shop.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-1">
              <div className="flex-shrink-0 w-8 h-8">
                {shop.iconUrl ? (
                  <img 
                    src={getOptimizedShopIcon.small(shop.iconUrl)} 
                    srcSet={generateImageKitSrcSet(shop.iconUrl, [32, 64])}
                    sizes="32px"
                    alt={`${shop.name} logo`}
                    className="w-8 h-8 rounded object-cover border border-gray-200 dark:border-gray-600"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const nextElement = e.currentTarget.nextElementSibling;
                      if (nextElement) {
                        (nextElement as HTMLElement).style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div 
                  className={`w-8 h-8 rounded border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 flex items-center justify-center ${shop.iconUrl ? 'hidden' : 'flex'}`}
                  title={shop.iconUrl ? undefined : "No logo"}
                >
                  <Store size={14} className="text-gray-400 dark:text-gray-500" />
                </div>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{shop.name}</h2>
            </div>
            <div className="flex flex-wrap gap-1 mt-1">
              {shop.tags?.map((tag) => (
                <TagBadge key={tag} tag={tag} />
              ))}
              {(!shop.tags || shop.tags.length === 0) && <span className="text-gray-400 dark:text-gray-500 text-xs">No tags</span>}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-300 mt-1">{shop.address}</div>
          </div>
          <div className="flex flex-col gap-2 ml-4">
            <button onClick={() => onEdit(shop)} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
              <Edit size={16} />
            </button>
            <button onClick={() => shop.id && onDelete(shop.id)} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300">
              <Trash2 size={16} />
            </button>
          </div>
        </div>
        {shop.description && <div className="text-sm text-gray-600 dark:text-gray-300 mt-2">{shop.description}</div>}
      </div>
    ))}
  </div>
);

export default ShopCardList;
